import { handle400Statuses, sendApiRequest } from '@services/api';
import { logNetRequest } from '@utils/usefulFuncs';

// Score Spec authoring CRUD. Wraps the Rails /api/v2/genai/score_specs surface (which proxies
// fapi). Mirrors src/services/scoreBackfills.js: every function calls sendApiRequest, logs,
// runs handle400Statuses (global 401/403), branches on `status === 200 && data`, and returns a
// flat { status, <payload>, error } object — it never throws. The server's 422/409/404 message
// lives in data.error (NOT the transport `error`), so we prefer `data?.error` first.

const BASE_URL = 'v2/genai/score_specs'; // sendApiRequest prepends `${BASEURL}/api/`

export const SPEC_STATUS = { DRAFT: 'draft', LOCKED: 'locked', APPROVED: 'approved', RETIRED: 'retired' };
export const SPEC_SOURCE = { API: 'api', SEED: 'seed' };
export const SCOPE_OPTIONS = { FULL_HISTORY: 'full_history', LATEST_PER_COMPANY: 'latest_per_company' };
export const UNIVERSE_KINDS = { WHOLE_CORPUS: 'whole_corpus', INDEX: 'index', TICKERS: 'tickers' };

// Per-ROW outcome on a preview run (fapi #492). A 200 can now carry a mix: `failed` means the
// model ran and its output could not be extracted for that company, `failed_resolve` means the
// occasion never resolved to anything to read. They point at opposite halves of the spec, so they
// are never collapsed into one "it didn't work" state. Naming mirrors BACKFILL_STATUSES.
export const PREVIEW_STATUS = { COMPLETED: 'completed', FAILED: 'failed', FAILED_RESOLVE: 'failed_resolve' };

// The server's hard cap on company-units per preview call. The request carries no `limit` any
// more, so this is reported, never enforced.
export const PREVIEW_MAX_UNITS = 25;

// Distinguishable per-status fallbacks, used only when the server sends no data.error. 403 is
// intentionally absent — handle400Statuses handles loss-of-permission globally.
const createErrorFallback = (status) => {
  if (status === 404) return 'That spec no longer exists — refreshing the list.';
  if (status === 409) return 'The spec changed since you loaded it. Refresh and try again.';
  if (status === 422) return 'The spec is invalid — see the details.';
  if (status === 502) return 'The scoring engine is unreachable. Try again shortly.';
  return 'Something went wrong. Please try again.';
};

// A 409 from approve is NOT the optimistic-concurrency 409 every other route returns: since PR #459
// it also means a feed name this spec publishes is already published by an approved spec. That
// failure is terminal for the row — feed-name uniqueness is enforced only across the approved set
// and only at approve, there is no unlock endpoint, and the spec is already frozen — so the fallback
// has to name the two ways out instead of suggesting a retry that cannot work.
const APPROVE_FALLBACKS = {
  409: 'A feed name this spec publishes is already taken by an approved spec. Approval cannot be retried: retire the spec that owns the name, or start a new draft with a different scores[].feed_name.',
};

// The shared 404/409 copy is spec-lifecycle flavoured ('That spec no longer exists — refreshing
// the list') and reads wrong on a preview run, which refreshes nothing. Overriding per-route with
// a fallbacks map is the established shape here — see APPROVE_FALLBACKS — and leaves the other
// eight functions' copy alone.
const PREVIEW_FALLBACKS = {
  404: 'That spec no longer exists — nothing was run.',
  409: 'This spec is no longer a draft, so it cannot be previewed. Reload to see where it now stands.',
  422: 'The engine refused this preview: either the saved spec does not compile, the universe and window are invalid, or this trigger kind cannot be previewed.',
};

const errorFrom = (data, error, status, fallbacks) =>
  data?.error || error || (fallbacks && fallbacks[status]) || createErrorFallback(status);

// --- deploy-skew detection -------------------------------------------------------------------
//
// Since fapi #492 the preview request is `{universe, from, to, scope}`; before it, `{doc_ids}`.
// The two are mutually incompatible, so between the two deploys one side 422s the other. Left
// alone that lands as PREVIEW_FALLBACKS[422], which tells the operator their spec is broken when
// nothing is wrong with it. FastAPI names the field it is refusing in `detail[].loc`, which
// survives any rewording of `msg` — the same reasoning as blamesColumnsField in services/feedView.
// `doc_ids` is the only name that is evidence ON ITS OWN: this editor never sends it, so a server
// asking about it can only be one running the pre-#492 contract. `universe` is NOT — the engine
// rejects a bad ticker or an unknown index by naming that same field, and misreporting those as a
// deploy problem would both blame the wrong party and discard the server's own message. It counts
// only as a whole-key rejection (`extra: forbid` on a body the server does not accept at all),
// which shows up as a two-element loc of exactly ['body', 'universe'].
const locNamesDocIds = (entry) =>
  Array.isArray(entry?.loc) && entry.loc.some((part) => String(part).toLowerCase() === 'doc_ids');

const locRejectsUniverseKey = (entry) =>
  Array.isArray(entry?.loc) &&
  entry.loc.length === 2 &&
  String(entry.loc[0]).toLowerCase() === 'body' &&
  String(entry.loc[1]).toLowerCase() === 'universe';

const locNamesPreviewField = (entry) => locNamesDocIds(entry) || locRejectsUniverseKey(entry);

const proseNamesPreviewField = (value) => typeof value === 'string' && /\bdoc_ids\b/i.test(value);

const blamesPreviewContract = (data) => {
  const detail = data?.detail;
  if (Array.isArray(detail)) {
    return detail.some((entry) => locNamesPreviewField(entry) || proseNamesPreviewField(entry?.msg));
  }
  if (detail && typeof detail === 'object') {
    return locNamesPreviewField(detail) || proseNamesPreviewField(detail.msg || detail.message);
  }
  return [detail, data?.error, data?.message].some(proseNamesPreviewField);
};

const CONTRACT_SKEW_MESSAGE =
  'The scoring engine is on a different preview contract than this editor — it is asking about fields the editor no longer sends. Nothing ran and nothing was charged. Reload; if it persists, tell the API team.';

// --- preview result normalization ------------------------------------------------------------
//
// Total: every access guarded, no throw reachable from any row shape. The dock has no local error
// boundary — app/App.js:73 mounts one at the ROOT — so a TypeError here replaces the entire editor
// and destroys both the unsaved buffer and the run that was just paid for.
//
// Reads the new field names ONLY. A pre-#492 row therefore normalizes to a row with nothing in it,
// which is the behaviour we want: a half-finished rename renders visibly empty instead of quietly
// rendering stale data under a new label.
const objectOrEmpty = (value) => (value && typeof value === 'object' && !Array.isArray(value) ? value : {});

// error_class and error_text are rendered as text. A server that sends a structured payload under
// either would otherwise reach the DOM as '[object Object]'; JSON is at least readable, and the
// null-vs-present distinction the card gates on is preserved.
const asDisplayText = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value);
  } catch (e) {
    return String(value);
  }
};

export const normalizePreviewResult = (row, index) => {
  const source = row && typeof row === 'object' ? row : {};
  const companyId = source.company_id ?? null;
  const triggerKey = source.trigger_key ?? null;
  return {
    // A company can appear on several rows under scope=full_history and the contract promises no
    // uniqueness for trigger_key, so neither field alone is a key — same rule, and same shape, as
    // services/feedView.js:157-160. The index makes a collision impossible; without it two paid
    // results silently collapse into one rendered card.
    key: `${triggerKey ?? 'nokey'}-${companyId ?? 'nocompany'}-${index}`,
    triggerKey,
    companyId,
    ticker: source.ticker ?? null,
    companyName: source.company_name ?? null,
    status: source.status ?? null,
    // null and {} are different: null is "extraction produced nothing", {} is a completed run that
    // recorded no score. Only a real object survives as scores.
    scores: source.scores && typeof source.scores === 'object' && !Array.isArray(source.scores) ? source.scores : null,
    signalPayload: objectOrEmpty(source.signal_payload),
    perScorePayloads: objectOrEmpty(source.per_score_payloads),
    // Preserved as null when absent — "ran on no documents" and "the engine did not say" are
    // different sentences in the dock.
    docIds: Array.isArray(source.doc_ids) ? source.doc_ids : null,
    sourcePublishedAt: source.source_published_at ?? null,
    maxContextPublishedAt: source.max_context_published_at ?? null,
    rawOutput: source.raw_output ?? null,
    errorClass: asDisplayText(source.error_class),
    errorText: asDisplayText(source.error_text),
  };
};

// GET / — authoring list, all statuses. Optional status/source filters map to query params.
export const listSpecs = async ({ status: statusFilter, source } = {}) => {
  const query = [statusFilter && `status=${statusFilter}`, source && `source=${source}`].filter(Boolean).join('&');
  const url = query ? `${BASE_URL}?${query}` : BASE_URL;
  const { status, data, error } = await sendApiRequest('get', url);
  logNetRequest(url, status, data, error);
  handle400Statuses(status);

  if (status === 200 && data) return { status, specs: data.score_specs || [], error: null };
  return { status, specs: [], error: errorFrom(data, error, status) };
};

// POST / — create a draft from a spec definition.
export const createSpec = async (definition) => {
  const { status, data, error } = await sendApiRequest('post', BASE_URL, definition);
  logNetRequest(BASE_URL, status, data, error);
  handle400Statuses(status);

  if (status === 200 && data) return { status, spec: data.score_spec, error: null };
  return { status, spec: null, error: errorFrom(data, error, status) };
};

// GET /:id — one spec, full record incl. spec_json.
export const getSpec = async (id) => {
  const url = `${BASE_URL}/${id}`;
  const { status, data, error } = await sendApiRequest('get', url);
  logNetRequest(url, status, data, error);
  handle400Statuses(status);

  if (status === 200 && data) return { status, spec: data.score_spec, error: null };
  return { status, spec: null, error: errorFrom(data, error, status) };
};

// PATCH /:id — full replacement of a draft's definition.
export const replaceSpec = async (id, definition) => {
  const url = `${BASE_URL}/${id}`;
  const { status, data, error } = await sendApiRequest('patch', url, definition);
  logNetRequest(url, status, data, error);
  handle400Statuses(status);

  if (status === 200 && data) return { status, spec: data.score_spec, error: null };
  return { status, spec: null, error: errorFrom(data, error, status) };
};

// DELETE /:id — hard delete (draft) or soft-retire (locked/approved). Two 200 shapes: a deleted
// draft returns { deleted, score_spec_id }; a retire returns { score_spec }. Branch on the key.
export const deleteOrRetireSpec = async (id) => {
  const url = `${BASE_URL}/${id}`;
  const { status, data, error } = await sendApiRequest('delete', url);
  logNetRequest(url, status, data, error);
  handle400Statuses(status);

  if (status === 200 && data) {
    return { status, deleted: data.deleted === true, spec: data.score_spec || null, error: null };
  }
  return { status, deleted: false, spec: null, error: errorFrom(data, error, status) };
};

// POST /:id/preview_score — synchronous dry-run over a universe + window (nothing persisted).
//
// Since fapi #492 the body is `preview_documents`' body minus `limit`, and the response is one row
// per COMPANY-UNIT rather than per document, capped at PREVIEW_MAX_UNITS server-side.
//
// Returns `run` (an envelope) rather than a bare `results` array, because `truncated`, `count`,
// `model` and the resolved window are facts about the RUN that no row can carry — and an array has
// nowhere to put them. `run: null` on failure preserves the truthiness branch the dock uses to
// tell "the call failed" from "the call ran and selected nothing".
//
// A 200 whose rows all failed is still a 200. Per-row failure is per-row; it never becomes the
// call's error.
export const previewScore = async (id, body) => {
  const url = `${BASE_URL}/${id}/preview_score`;
  const { status, data, error } = await sendApiRequest('post', url, body);
  logNetRequest(url, status, data, error);
  handle400Statuses(status);

  if (status === 200 && data) {
    return {
      status,
      run: {
        results: Array.isArray(data.results) ? data.results.map(normalizePreviewResult) : [],
        // Deliberately NOT differenced against results.length anywhere: whether `count` is the
        // rows returned or the pre-cap selection size is unconfirmed with fapi, and `truncated`
        // answers the only question the copy actually needs.
        count: typeof data.count === 'number' ? data.count : null,
        truncated: data.truncated === true,
        model: data.model ?? null,
        resolvedFrom: data.resolved_from ?? null,
        resolvedTo: data.resolved_to ?? null,
      },
      error: null,
    };
  }
  if (status === 422 && blamesPreviewContract(data)) {
    return { status, run: null, error: CONTRACT_SKEW_MESSAGE };
  }
  return { status, run: null, error: errorFrom(data, error, status, PREVIEW_FALLBACKS) };
};

// POST /:id/preview_documents — resolve which documents/occasions a universe+window selects.
export const previewDocuments = async (id, body) => {
  const url = `${BASE_URL}/${id}/preview_documents`;
  const { status, data, error } = await sendApiRequest('post', url, body);
  logNetRequest(url, status, data, error);
  handle400Statuses(status);

  if (status === 200 && data) return { status, preview: data, error: null };
  // Same preview-flavoured copy as previewScore: this is now the free pre-flight in front of it,
  // and the shared 404/409 wording ('refreshing the list') describes a screen that isn't there.
  return { status, preview: null, error: errorFrom(data, error, status, PREVIEW_FALLBACKS) };
};

// Empty-body lifecycle transitions — each returns the updated <SpecRecord>.
const transition = async (id, action, fallbacks) => {
  const url = `${BASE_URL}/${id}/${action}`;
  const { status, data, error } = await sendApiRequest('post', url);
  logNetRequest(url, status, data, error);
  handle400Statuses(status);

  if (status === 200 && data) return { status, spec: data.score_spec, error: null };
  return { status, spec: null, error: errorFrom(data, error, status, fallbacks) };
};

export const lockSpec = (id) => transition(id, 'lock');
export const approveSpec = (id) => transition(id, 'approve', APPROVE_FALLBACKS);
export const enableLive = (id) => transition(id, 'enable_live');
export const disableLive = (id) => transition(id, 'disable_live');
